import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import { TextField, SelectField, TextAreaField } from "@/components/Field";
import { submitQuote } from "@/lib/api";
import { SERVICE_OPTIONS, CONTACT } from "@/lib/data";

const EXPECT = [
  "A single point of contact for your freight",
  "Carriers matched to your equipment, lane and timing",
  "Clear rate confirmation before booking",
  "Status updates during transit",
  "Proof-of-delivery follow-up",
];

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  origin: "", destination: "", service_type: "",
  weight_dimensions: "", pickup_date: "", equipment_needs: "", details: "",
};

export default function Shippers() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitQuote(form);
      toast.success("Quote request received. Dispatch will respond shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Submission failed. Please try again or email dispatch directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-16" data-testid="shippers-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>// For Shippers &amp; Businesses</SectionLabel>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl lg:text-6xl">
            Your freight. <span className="text-hazard">Our coordination.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg">
            Freight sourcing, rate coordination, pickup and delivery scheduling, tracking and document follow-up — handled by one professional point of contact.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-5 lg:px-8">
        <Reveal className="lg:col-span-2">
          <h2 className="font-display text-2xl font-extrabold uppercase text-bright sm:text-3xl">What you can expect from ELT</h2>
          <div className="mt-8 flex flex-col gap-4">
            {EXPECT.map((item, i) => (
              <Reveal key={item} delay={i * 0.06} y={12}>
                <div className="flex items-start gap-3 border border-line bg-panel p-4">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-hazard" />
                  <p className="text-sm leading-relaxed text-[#C9CDD4]">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 border border-line bg-panel p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brass">Prefer email?</p>
            <a href={`mailto:${CONTACT.dispatch}`} data-testid="shippers-email-link" className="mt-2 block font-mono text-sm text-hazard hover:underline">
              {CONTACT.dispatch}
            </a>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.1}>
          <form onSubmit={onSubmit} className="border border-line bg-panel" data-testid="quote-form">
            <div className="hazard-stripes h-1.5 w-full" />
            <div className="p-7 sm:p-9">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-2xl font-extrabold uppercase text-bright">Request a Quote</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">Form RFQ-01</span>
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <TextField label="Contact Name" name="name" value={form.name} onChange={onChange} required testId="quote-name-input" />
                <TextField label="Company" name="company" value={form.company} onChange={onChange} testId="quote-company-input" />
                <TextField label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="quote-email-input" />
                <TextField label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testId="quote-phone-input" />
                <TextField label="Origin / Pickup Location" name="origin" value={form.origin} onChange={onChange} required placeholder="City, Province/State" testId="quote-origin-input" />
                <TextField label="Destination / Delivery" name="destination" value={form.destination} onChange={onChange} required placeholder="City, Province/State" testId="quote-destination-input" />
                <SelectField label="Freight Service Type" name="service_type" value={form.service_type} onChange={onChange} options={SERVICE_OPTIONS} required testId="quote-service-select" />
                <TextField label="Target Pickup Date" name="pickup_date" type="date" value={form.pickup_date} onChange={onChange} testId="quote-date-input" />
                <TextField label="Weight & Dimensions" name="weight_dimensions" value={form.weight_dimensions} onChange={onChange} placeholder="e.g. 12 pallets, 18,000 lbs" testId="quote-weight-input" />
                <TextField label="Special Equipment Needs" name="equipment_needs" value={form.equipment_needs} onChange={onChange} placeholder="Tarps, straps, team drivers…" testId="quote-equipment-input" />
              </div>
              <div className="mt-5">
                <TextAreaField label="Freight Details" name="details" value={form.details} onChange={onChange} placeholder="Commodity, timing constraints, accessorials…" testId="quote-details-textarea" />
              </div>
              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={sending}
                className="mt-8 w-full bg-hazard px-6 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark disabled:opacity-50"
              >
                {sending ? "Transmitting…" : "Submit Quote Request"}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                Reviewed by dispatch — response within one business day
              </p>
            </div>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
